import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { useQuery } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import moment from 'moment';

import { SILVER_VOUCHER_DESCRIPTION, VOUCHER_TYPE_DISCOUNT } from 'constants/vouchers';
import { GET_VOUCHER_BY_TYPE_AND_PERIOD } from 'store/queries';
import { FullPageSpinner } from 'admin-components/components';
import AgentPortalError from 'errors/AgentPortalError';
import { ACTION_CODES } from 'constants/errors';
import SortableTable from 'components/ui/SortableTable';
import { colours } from 'themes/default';
import Select from 'components/ui/Select';
import { userShape } from 'constants/propTypes';
import { isDCOffice } from 'utils/user';
import config from 'utils/config';

import { StyledContainer, StyledSummaryContainer, StyledSearchContainer } from './SilverVouchersReport.styles';

const columns = [
  {
    key: 'createdAt',
    intlId: 'beMyGuestResults.createdAt',
    format: (createdAt) => moment(createdAt).format('ddd DD MMM YYYY')
  },
  {
    key: 'createdBy',
    intlId: 'beMyGuestResults.createdBy'
  },
  {
    key: 'reference',
    intlId: 'beMyGuestResults.reference',
    format: (reference) => (
      <Link target="_blank" to={`/discountCodes/${reference}`}>
        {reference}
      </Link>
    )
  }
];

const monthList = new Array(12).fill(null).map((_, index) => {
  const month = moment().subtract(index, 'months');
  const label = month.format('MMMM YYYY');
  const value = month.startOf('month').format('YYYY-MM-DD');

  return { label, value };
});

const SilverVouchersReport = ({ user }) => {
  const isGenerateDiscountCodeEnabled = config('ENABLE_GENERATE_DISCOUNT_CODE', { canOverride: true }) === 'true';

  const history = useHistory();
  const currentDate = new Date();

  const [toDate, setToDate] = useState(currentDate.toISOString().split('T')[0]);
  const [fromDate, setFromDate] = useState(monthList[0].value);

  useEffect(() => {
    if (!isGenerateDiscountCodeEnabled || !isDCOffice(user)) {
      history.push('/');
    }
  }, [user, history, isGenerateDiscountCodeEnabled]);

  const { loading, error, data, refetch } = useQuery(GET_VOUCHER_BY_TYPE_AND_PERIOD, {
    variables: {
      fromDate,
      toDate,
      active: true,
      description: SILVER_VOUCHER_DESCRIPTION,
      voucherType: VOUCHER_TYPE_DISCOUNT
    }
  });

  if (error) {
    throw new AgentPortalError(error, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      errorCTARedirect: '/silver-vouchers-report'
    });
  }

  if (loading) {
    return <FullPageSpinner data-testid="vouchers-silver-vouchers-report-loading" />;
  }

  const vouchers = data?.getVouchersByTypeAndPeriod?.vouchers ?? [];

  const handleChangeMonth = ({ target: { value } }) => {
    let toDateValue = currentDate.toISOString().split('T')[0];
    const to = moment(value);

    if (!moment(currentDate).isSame(to, 'month')) {
      toDateValue = to.endOf('month').format('YYYY-MM-DD');
    }

    setFromDate(value);
    setToDate(toDateValue);

    refetch({
      variables: {
        fromDate: value,
        toDate: toDateValue,
        active: true,
        description: SILVER_VOUCHER_DESCRIPTION,
        voucherType: VOUCHER_TYPE_DISCOUNT
      }
    });
  };

  return (
    <StyledContainer>
      <StyledSummaryContainer>
        <StyledSearchContainer>
          <h1 data-testid="silver-vouchers-report-title">
            <FormattedMessage id="silverVouchersResults.title" />
          </h1>
          <Select id="searchMonth" data-cy="search-month" data-testid="search-month" defaultValue={fromDate} onChange={handleChangeMonth}>
            {monthList.map(({ label, value }) => (
              <option key={label} value={value} data-testid={`search-month-${value}`}>
                {label}
              </option>
            ))}
          </Select>
        </StyledSearchContainer>
        <span data-testid="silver-vouchers-report-result-message">
          <FormattedMessage id="bookingSearchResults.summary.weFound" /> {vouchers?.length ?? 0}{' '}
          <FormattedMessage id="bookingSearchResults.summary.resultsFor" />: <FormattedMessage id="beMyGuestResults.cratedFromDate" /> ={' '}
          {moment(fromDate).format('ddd DD MMM YYYY')}, <FormattedMessage id="beMyGuestResults.cratedToDate" /> ={' '}
          {moment(toDate).format('ddd DD MMM YYYY')}.
        </span>
      </StyledSummaryContainer>
      {vouchers?.length > 0 && (
        <SortableTable
          formatHeader={({ intlId }) => <FormattedMessage id={intlId} />}
          data={vouchers}
          columns={columns}
          sortKey="createdAt"
          borderColor={colours.Greyscale.CharcoalGrey}
          alternateBackground={colours.Messaging.SuccessGreenLight}
        />
      )}
    </StyledContainer>
  );
};

SilverVouchersReport.propTypes = {
  user: userShape
};

export default SilverVouchersReport;
